import { useQuery } from "@tanstack/react-query";
import useRetryFetch from "./useRetryFetch";
import { config } from "../Environment";

interface FiltersType {
  search: string;
  categories: string[];
  tags: string[];
  difficulty: string[];
  estimatedTimeMin: number | undefined;
  estimatedTimeMax: number | undefined;
  priceMin: number | undefined;
  priceMax: number | undefined;
  vendorId: string | undefined;
}

interface UseProductSearchOptions {
  type: "activity" | "service" | "bundle" | "pwf" | "program";
  filters: FiltersType;
  vendorId?: string;
  includeItems?: boolean;
}

// Module-level cache for vendor logos (persists across hook instances)
const vendorLogoCache: Record<string, string | null> = {};

export async function enrichVendorLogos(
  products: any[],
  retryFetch: ReturnType<typeof useRetryFetch>
) {
  // Collect unique vendor IDs that aren't cached yet
  const uncachedIds = Array.from(
    new Set(
      products
        .map((p: any) => p.fiVendor?._id)
        .filter((id: any): id is string => !!id && !(id in vendorLogoCache))
    )
  );

  // Fetch uncached vendor info in parallel
  if (uncachedIds.length > 0) {
    await Promise.all(
      uncachedIds.map(async (id) => {
        try {
          const res = await retryFetch(
            `${config.API_BASE}/api/vendor/findActive?id=${id}`
          );
          const data = await res.json();
          vendorLogoCache[id] = data.item?.logoURL || null;
        } catch {
          vendorLogoCache[id] = null;
        }
      })
    );
  }

  // Merge logoURL into each product's fiVendor
  return products.map((product) => {
    const vendorId = product.fiVendor?._id;
    const logoURL = vendorId ? vendorLogoCache[vendorId] : null;
    return {
      ...product,
      vendorName: product.fiVendor?.displayName || "",
      fiVendor: product.fiVendor
        ? { ...product.fiVendor, logoURL: product.fiVendor.logoURL || logoURL }
        : product.fiVendor,
    };
  });
}

/**
 * Shared hook for marketplace product search.
 * Uses React Query for automatic deduplication, caching, and background refetch.
 */
export function useProductSearch({
  type,
  filters,
  vendorId,
  includeItems,
}: UseProductSearchOptions) {
  const retryFetch = useRetryFetch();

  return useQuery<any[]>({
    queryKey: ["products", "searchActive", type, vendorId, filters],
    queryFn: async () => {
      const query = new URLSearchParams();
      query.set("type", type);
      query.set("includeVendor", "1");

      if (includeItems) {
        query.set("includeItems", "1");
      }
      if (vendorId) {
        query.set("vendorId", vendorId);
      }

      // Add filters to API query
      if (filters.search) {
        query.set("search", filters.search);
      }
      if (filters.categories.length > 0) {
        query.set("categories", filters.categories.join(","));
      }
      if (filters.tags.length > 0) {
        query.set("tags", filters.tags.join(","));
      }
      if (filters.difficulty.length > 0) {
        query.set("difficulty", filters.difficulty.join(","));
      }
      if (filters.estimatedTimeMin !== undefined) {
        query.set("estimatedTimeMin", filters.estimatedTimeMin.toString());
      }
      if (filters.estimatedTimeMax !== undefined) {
        query.set("estimatedTimeMax", filters.estimatedTimeMax.toString());
      }
      if (filters.priceMin !== undefined) {
        query.set("priceMin", filters.priceMin.toString());
      }
      if (filters.priceMax !== undefined) {
        query.set("priceMax", filters.priceMax.toString());
      }
      if (filters.vendorId) {
        query.set("vendorId", filters.vendorId);
      }

      const response = await retryFetch(
        `${config.API_BASE}/api/product/searchActive?${query.toString()}`
      );
      const result = await response.json();

      if (Array.isArray(result.list)) {
        return enrichVendorLogos(result.list, retryFetch);
      }
      return [];
    },
  });
}
